import { compareRetailerPreference } from '../../domain/retailerPreference';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getProductBySlug,
  getOffersByProductId,
  mergeRelatedOffersIntoDisplayList,
} from '../../utils/api';
import NotFoundPage from '../notFound/NotFoundPage';
import { isPriceFirstPdpSlug } from './pdpPricePageView';
import SimpleProductPage from './SimpleProductPage';

/**
 * DB PDP: load `products` row by slug, then load offers via `getOffersByProductId(product.id)` only.
 * No lookup-name / csvLookupId pricing on this route.
 */
const ProductComparisonPage = () => {
  const { slug, productId } = useParams();
  /** `/simple-products/db/:slug` → `slug`; `/simple-products/:productId` (DB entry) → `productId`. */
  const resolvedSlug = slug ?? productId ?? null;

  const [product, setProduct] = useState(null);
  const [offers, setOffers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!resolvedSlug) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);
    setProduct(null);
    setOffers(null);

    const load = async () => {
      try {
        const productData = await getProductBySlug(resolvedSlug);
        if (cancelled) return;
        if (!productData) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setProduct(productData);
        const offersResult = await getOffersByProductId(productData.id);
        if (cancelled) return;
        const safeOffers = Array.isArray(offersResult?.data)
          ? offersResult.data
              .filter(
                (item) =>
                  typeof item?.numeric_price === 'number' &&
                  Number.isFinite(item.numeric_price) &&
                  item.numeric_price > 0
              )
              .sort((a, b) => a.numeric_price - b.numeric_price || compareRetailerPreference(a.Source, b.Source))
          : [];
        const displayOffers = isPriceFirstPdpSlug(productData.slug)
          ? safeOffers
          : await mergeRelatedOffersIntoDisplayList(resolvedSlug, safeOffers);
        if (cancelled) return;
        setOffers(displayOffers);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Something went wrong');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [resolvedSlug]);

  if (loading) {
    return (
      <div className="ab-gear-page" style={{ padding: '2rem', textAlign: 'center' }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="ab-gear-page" style={{ padding: '2rem', textAlign: 'center' }}>
        {error}
      </div>
    );
  }

  if (notFound || !product) {
    return <NotFoundPage />;
  }

  return (
    <SimpleProductPage
      specificProductId={product.id}
      productSlug={product.slug}
      productBrand={product.brand}
      productName={product.name}
      productImage={product.image_url ?? product.imageURL}
      prefetchedOffers={offers}
      msrp={product.msrp}
      mpn={product.mpn}
    />
  );
};

export default ProductComparisonPage;
